import { c as _c } from "react/compiler-runtime";
import { add } from 'date-fns';
import { useFormikContext } from 'formik';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { Datepicker, FormErrorMessage, NumberInput } from 'ui';
import { getTripLengthInDays } from 'utils/DateFormats';
import useJourneyData from '~/hooks/useJourneyData';
import type { JourneyEditFormValues } from './JourneyEditForm.types';
type DurationLimiter = {
  minDurationDays: number;
  maxDurationDays: number;
};
type DatesProps = {
  simpleDatePicker?: boolean;
  durationLimiter: DurationLimiter;
};
const Dates = t0 => {
  const $ = _c(26);
  const {
    simpleDatePicker,
    durationLimiter
  } = t0;
  const locale = useLocale();
  const {
    futurePricingTiers,
    unavailableDaysOfWeek,
    unavailableDates
  } = useJourneyData();
  const {
    values,
    setFieldValue,
    errors
  } = useFormikContext();
  let t1;
  if ($[0] !== setFieldValue) {
    t1 = t2 => {
      const {
        startDate,
        endDate
      } = t2;
      if (startDate && endDate) {
        setFieldValue("duration", getTripLengthInDays(startDate, endDate));
      }
    };
    $[0] = setFieldValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setDuration = t1;
  let t2;
  if ($[2] !== values.duration) {
    t2 = startDate_0 => {
      const duration = values.duration;
      return add(startDate_0, {
        days: duration - 1
      });
    };
    $[2] = values.duration;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const setNewEndDate = t2;
  let t3;
  if ($[4] !== errors.duration || $[5] !== errors.startDate) {
    t3 = (errors.startDate || errors.duration) && <div className="px-4 mt-2 md:px-2"><FormErrorMessage name={errors.startDate ? "startDate" : "duration"} /></div>;
    $[4] = errors.duration;
    $[5] = errors.startDate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== futurePricingTiers?.pricingTiers || $[8] !== locale || $[9] !== setDuration || $[10] !== setFieldValue || $[11] !== setNewEndDate || $[12] !== simpleDatePicker || $[13] !== unavailableDates?.dates || $[14] !== unavailableDaysOfWeek || $[15] !== values.endDate || $[16] !== values.startDate) {
    t4 = simpleDatePicker ? <Datepicker isSimpleDatePicker={true} range={true} selected={[values.startDate, values.endDate]} onChange={t5 => {
      const [startDate_1] = t5;
      setFieldValue("startDate", startDate_1);
      setFieldValue("endDate", setNewEndDate(startDate_1));
    }} unavailableDaysOfWeek={unavailableDaysOfWeek} futurePricingAvailability={futurePricingTiers?.pricingTiers} unavailableDates={unavailableDates?.dates} locale={locale} /> : <Datepicker range={true} minBookingDays={1} selected={[values.startDate, values.endDate]} onChange={t6 => {
      const [startDate_2, endDate_0] = t6;
      setFieldValue("startDate", startDate_2);
      setFieldValue("endDate", endDate_0);
      setDuration({
        startDate: startDate_2,
        endDate: endDate_0
      });
    }} unavailableDaysOfWeek={unavailableDaysOfWeek} futurePricingAvailability={futurePricingTiers?.pricingTiers} unavailableDates={unavailableDates?.dates} locale={locale} />;
    $[7] = futurePricingTiers?.pricingTiers;
    $[8] = locale;
    $[9] = setDuration;
    $[10] = setFieldValue;
    $[11] = setNewEndDate;
    $[12] = simpleDatePicker;
    $[13] = unavailableDates?.dates;
    $[14] = unavailableDaysOfWeek;
    $[15] = values.endDate;
    $[16] = values.startDate;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== durationLimiter || $[19] !== simpleDatePicker || $[20] !== values.startDate) {
    t5 = values.startDate && !simpleDatePicker && <><hr /><DaysSpinner minMaxDays={durationLimiter} /></>;
    $[18] = durationLimiter;
    $[19] = simpleDatePicker;
    $[20] = values.startDate;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t3 || $[23] !== t4 || $[24] !== t5) {
    t6 = <div>{t3}{t4}{t5}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
function DaysSpinner(t0) {
  const $ = _c(22);
  const {
    minMaxDays
  } = t0;
  const {
    values,
    setFieldValue
  } = useFormikContext();
  const t = useTranslations("journey");
  const {
    startDate,
    duration
  } = values;
  const {
    minDurationDays,
    maxDurationDays
  } = minMaxDays;
  let t1;
  let t2;
  if ($[0] !== duration || $[1] !== setFieldValue || $[2] !== startDate) {
    t1 = () => {
      if (!duration || !startDate) {
        return;
      }
      setFieldValue("endDate", add(startDate, {
        days: duration - 1
      }));
    };
    t2 = [duration, startDate, setFieldValue];
    $[0] = duration;
    $[1] = setFieldValue;
    $[2] = startDate;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== t) {
    t3 = t("duration_label");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="typography-emphasis-2">{t3}</div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== maxDurationDays || $[10] !== minDurationDays || $[11] !== t) {
    t5 = t("duration_range_help", {
      min: minDurationDays,
      max: maxDurationDays
    });
    $[9] = maxDurationDays;
    $[10] = minDurationDays;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <p className="typography-body-2 text-neutral-600">{t5}</p>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== maxDurationDays || $[16] !== minDurationDays) {
    t7 = <NumberInput min={minDurationDays} max={maxDurationDays} showErrors={false} name="duration" />;
    $[15] = maxDurationDays;
    $[16] = minDurationDays;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t6 || $[20] !== t7) {
    t8 = <div className="px-4 pt-4 text-sm">{t4}{t6}{t7}</div>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
export { Dates };